"use client";

import React, { useEffect, useState } from "react";

const getInitialForm = () => ({
  fname: "",
  lname: "",
  phone: "",
  email: "",
  date: "",
  guests: "",
  message: "",
  noOfRooms: "0",
  roomsRequired: false,
  checkIn: "",
  checkOut: "",
});

export default function BookTableModal({
  show,
  onClose,
  hotelName,
  cityId: cityIdProp,
  propertyId: propertyIdProp,
  propertyData,
  backgroundColor,
}) {
  const [form, setForm] = useState(getInitialForm);

  const [ids, setIds] = useState({
    cityId: cityIdProp ? String(cityIdProp) : "",
    propertyId: propertyIdProp ? String(propertyIdProp) : "",
  });

  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState("");

  // Today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split("T")[0];

  // Reset form every time modal opens
  useEffect(() => {
    if (show) {
      setForm(getInitialForm());
      setNotice("");
      setLoading(false);
    }
  }, [show]);

  // Update IDs whenever property information changes
  useEffect(() => {
    const nextCityId =
      propertyData?.cityId != null
        ? String(propertyData.cityId)
        : cityIdProp != null
          ? String(cityIdProp)
          : "";

    const nextPropertyId =
      propertyData?.propertyId != null
        ? String(propertyData.propertyId)
        : propertyIdProp != null
          ? String(propertyIdProp)
          : "";

    setIds({
      cityId: nextCityId,
      propertyId: nextPropertyId,
    });
  }, [propertyData, cityIdProp, propertyIdProp]);

  // Handle form input changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;

    let updatedValue = value;

    // First Name / Last Name
    // Only letters and spaces
    if (name === "fname" || name === "lname") {
      updatedValue = value.replace(/[^a-zA-Z\s]/g, "");
    }

    // Phone
    // Only digits + maximum 10 digits
    if (name === "phone") {
      updatedValue = value.replace(/\D/g, "").slice(0, 10);
    }

    // No. of Guests
    // Only digits + maximum 3 digits + no leading zero
    if (name === "guests") {
      updatedValue = value.replace(/\D/g, "").slice(0, 3);

      // Prevent leading zero
      if (updatedValue.length > 0) {
        updatedValue = updatedValue.replace(/^0+/, "");
      }

      // Maximum value 999
      if (updatedValue !== "" && Number(updatedValue) > 999) {
        updatedValue = "999";
      }
    }

    // Message
    // Maximum 200 characters
    if (name === "message") {
      updatedValue = value.slice(0, 200);
    }

    setForm((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));

    // Clear previous notice when user starts editing
    if (notice) {
      setNotice("");
    }
  };

  // Close modal and reset everything
  const handleClose = () => {
    setForm(getInitialForm());
    setNotice("");
    setLoading(false);
    onClose();
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();

    // First Name validation
    if (!form.fname.trim()) {
      setNotice("Please enter your first name.");
      return;
    }

    if (!/^[a-zA-Z\s]+$/.test(form.fname)) {
      setNotice("First name can contain only letters and spaces.");
      return;
    }

    // Last Name validation
    if (!form.lname.trim()) {
      setNotice("Please enter your last name.");
      return;
    }

    if (!/^[a-zA-Z\s]+$/.test(form.lname)) {
      setNotice("Last name can contain only letters and spaces.");
      return;
    }

    // Phone validation
    if (!/^\d{10}$/.test(form.phone)) {
      setNotice("Please enter a valid 10-digit phone number.");
      return;
    }

    // Guests validation
    if (!form.guests || Number(form.guests) < 1) {
      setNotice("Number of guests must be at least 1.");
      return;
    }

    if (Number(form.guests) > 999) {
      setNotice("Number of guests cannot exceed 999.");
      return;
    }

    // Message validation
    if (!form.message.trim()) {
      setNotice("Please enter a message.");
      return;
    }

    if (form.message.length > 200) {
      setNotice("Message cannot exceed 200 characters.");
      return;
    }

    // Date validation
    if (form.date && form.date < today) {
      setNotice("Please select a valid date.");
      return;
    }

    setLoading(true);
    setNotice("");

    const payload = {
      cityId: String(ids.cityId || ""),
      propertyId: String(ids.propertyId || ""),
      firstName: String(form.fname || ""),
      lastName: String(form.lname || ""),
      mobileNo: String(form.phone || ""),
      email: String(form.email || ""),
      eventDate: String(form.date || ""),
      eventType: "Dinning",
      roomsRequired: Boolean(form.roomsRequired),
      noOfRooms: String(form.noOfRooms || "0"),
      noOfGuests: String(form.guests || "0"),
      checkIn: String(form.checkIn || form.date || ""),
      checkOut: String(form.checkOut || form.date || ""),
      requestFrom: "Dinning Reservation",
      remarks: String(form.message || "Dining"),
      remarks1: hotelName || "",
      remarks2: "",
    };

    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_ORANGE_TIGER_BASE_URL}/Api/common/EnquireNow`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      let data = null;

      try {
        data = await res.clone().json();
      } catch {
        // Response is not JSON
      }

      // HTTP error
      if (!res.ok) {
        const msg =
          data?.message ||
          data?.error ||
          `HTTP ${res.status}`;

        setNotice(`Failed to submit: ${msg}`);
        return;
      }

      // API-level error
      if (
        data &&
        typeof data === "object" &&
        "errorCode" in data &&
        String(data.errorCode) !== "0"
      ) {
        setNotice(
          data.errorMessage ||
            "Something went wrong."
        );
        return;
      }

      // Success
      setNotice(
        "Your booking enquiry has been submitted successfully."
      );

      // Close after 1.5 seconds.
      // handleClose() will also reset the form.
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      console.error(err);

      setNotice(
        "Failed to send enquiry. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  // Don't render modal when closed
  if (!show) {
    return null;
  }

  return (
    <>
      <div
        className="modal fade show"
        tabIndex="-1"
        style={{
          display: "block",
          background: "rgba(0,0,0,0.5)",
        }}
      >
        <div className="modal-dialog">
          <div className="modal-content p-1">
            {/* Header */}
            <div className="modal-header">
              <h5 className="modal-title !text-[14px] fw-semibold">
                Enquiry for {hotelName}
              </h5>

              <button
                type="button"
                className="btn-close close-popup"
                aria-label="Close"
                onClick={handleClose}
                style={{
                  background: backgroundColor,
                }}
              >
                ×
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <div className="p-3">
                <div className="row">
                  {/* First Name */}
                  <div className="col-md-6">
                    <div className="mb-3 form-group">
                      <label className="form-label text-[14px]">
                        First Name
                      </label>

                      <input
                        type="text"
                        name="fname"
                        className="form-control"
                        value={form.fname}
                        onChange={handleFormChange}
                        required
                        pattern="[A-Za-z\s]+"
                        title="First name can contain only letters and spaces"
                      />
                    </div>
                  </div>

                  {/* Last Name */}
                  <div className="col-md-6">
                    <div className="mb-3 form-group">
                      <label className="form-label text-[14px]">
                        Last Name
                      </label>

                      <input
                        type="text"
                        name="lname"
                        className="form-control"
                        value={form.lname}
                        onChange={handleFormChange}
                        required
                        pattern="[A-Za-z\s]+"
                        title="Last name can contain only letters and spaces"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="col-md-6">
                    <div className="mb-3 form-group">
                      <label className="form-label text-[14px]">
                        Email
                      </label>

                      <input
                        type="email"
                        name="email"
                        className="form-control"
                        value={form.email}
                        onChange={handleFormChange}
                        required
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="col-md-6">
                    <div className="mb-3 form-group">
                      <label className="form-label text-[14px]">
                        Phone
                      </label>

                      <input
                        type="tel"
                        name="phone"
                        className="form-control"
                        value={form.phone}
                        onChange={handleFormChange}
                        maxLength={10}
                        inputMode="numeric"
                        pattern="[0-9]{10}"
                        required
                      />
                    </div>
                  </div>

                  {/* Date */}
                  <div className="col-md-6">
                    <div className="mb-3 form-group">
                      <label className="form-label text-[14px]">
                        Date
                      </label>

                      <input
                        type="date"
                        name="date"
                        className="form-control"
                        value={form.date}
                        onChange={handleFormChange}
                        min={today}
                        required
                      />
                    </div>
                  </div>

                  {/* Guests */}
                  <div className="col-md-6">
                    <div className="mb-3 form-group">
                      <label className="form-label text-[14px]">
                        No. of Guests
                      </label>

                      <input
                        type="text"
                        name="guests"
                        className="form-control"
                        value={form.guests}
                        onChange={handleFormChange}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={3}
                        required
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div className="col-md-12">
                    <div className="mb-0 form-group">
                      <label className="form-label text-[14px]">
                        Message
                      </label>

                      <textarea
                        name="message"
                        className="form-control"
                        value={form.message}
                        onChange={handleFormChange}
                        rows={3}
                        maxLength={200}
                        required
                      />

                      <div className="text-end mt-1">
                        <small className="text-muted">
                          {form.message.length}/200
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notice */}
                {notice && (
                  <p className="mt-2 text-danger">
                    {notice}
                  </p>
                )}
              </div>

              {/* Submit */}
              <div className="text-center mb-3 custom-submit-button">
                <button
                  type="submit"
                  className="btn btn-primary submit-btn"
                  style={{
                    background: backgroundColor,
                  }}
                  disabled={loading}
                >
                  {loading ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Backdrop */}
      <div className="modal-backdrop fade show"></div>

      {/* Styles */}
      <style jsx>{`
        .modal-content {
          border-bottom: 5px solid ${backgroundColor};
        }

        .close-popup {
          position: absolute;
          right: 10px;
          top: 10px;
          border: none;
          background: none;
          font-size: 1.7rem;
          cursor: pointer;
          color: #fff;
          line-height: 1rem;
          padding: 0;
          opacity: 1;
          background-color: ${backgroundColor};
        }

        .submit-btn {
          font-size: 12px;
          border-radius: 0;
          background: ${backgroundColor};
          padding: 7px 10px;
          border: none;
          color: #fff;
          cursor: pointer;
          text-transform: uppercase;
        }

        .submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .form-control:focus {
          box-shadow: none;
        }
      `}</style>
    </>
  );
}
